import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { endpoints } from "@/api";
import { useI18n } from "@/i18n";
import { useAdmin } from "@/hooks/useAdmin";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import AiSuggestButton from "@/components/AiSuggestButton";
import ImageUploader from "@/components/ImageUploader";

export default function BlogEditor() {
  const { t, isRTL } = useI18n();
  const serifClass = isRTL ? "font-serif-ar" : "font-serif-en";
  const isAdmin = useAdmin();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const { data: existing, isLoading: loadingExisting } = useQuery({
    queryKey: ["blog-edit", id],
    queryFn: async () => {
      const posts = await endpoints.listBlogPosts();
      return posts.find((p) => p.id === id);
    },
    enabled: isEdit,
  });

  if (!isAdmin) {
    return (
      <div className="max-w-2xl mx-auto p-12 text-center text-[#5C544F]">
        {isRTL ? "هذه الصفحة للمشرف فقط." : "Admin only."}
      </div>
    );
  }
  if (isEdit && loadingExisting) {
    return <div className="max-w-2xl mx-auto p-12 text-center text-[#5C544F]">…</div>;
  }
  return <Editor existing={existing} t={t} isRTL={isRTL} serifClass={serifClass} navigate={navigate} isEdit={isEdit} id={id} />;
}

function Editor({ existing, t, isRTL, serifClass, navigate, isEdit, id }) {
  const [form, setForm] = useState(() => ({
    title_en: existing?.title_en || "",
    title_ar: existing?.title_ar || "",
    slug: existing?.slug || "",
    excerpt_en: existing?.excerpt_en || "",
    excerpt_ar: existing?.excerpt_ar || "",
    content_en: existing?.content_en || "",
    content_ar: existing?.content_ar || "",
    cover_image: existing?.cover_image || "",
    tags: (existing?.tags || []).join(", "),
  }));
  const [submitting, setSubmitting] = useState(false);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        title_en: form.title_en,
        title_ar: form.title_ar,
        slug: form.slug || form.title_en,
        excerpt_en: form.excerpt_en,
        excerpt_ar: form.excerpt_ar,
        content_en: form.content_en,
        content_ar: form.content_ar,
        cover_image: form.cover_image || null,
        tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean),
      };
      if (isEdit) {
        await endpoints.updateBlogPost(id, payload);
        toast.success(isRTL ? "تم الحفظ" : "Saved");
        navigate(`/blog/${existing.slug}`);
      } else {
        const post = await endpoints.createBlogPost(payload);
        toast.success(isRTL ? "تم النشر" : "Published");
        navigate(`/blog/${post.slug}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || (isRTL ? "حدث خطأ" : "Something went wrong"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="max-w-3xl mx-auto px-6 lg:px-10 pt-12 pb-24" data-testid="blog-editor">
      <h1 className={`${serifClass} text-4xl text-[#2A2421] mb-8`}>
        {isEdit ? (isRTL ? "تعديل مقال" : "Edit post") : (isRTL ? "مقال جديد" : "New post")}
      </h1>

      <div className="mb-6 p-4 rounded-2xl bg-gradient-to-br from-[#C86240]/8 via-white to-[#F9F6F0] border border-[#C86240]/15 flex items-center justify-between gap-3" data-testid="ai-suggest-banner">
        <div className="text-xs text-[#5C544F] flex-1">
          {isRTL
            ? "اكتب المحتوى أولاً، ثم اضغط الزر ليقترح الذكاء الاصطناعي عنواناً ومقتطفاً ثنائي اللغة."
            : "Write content first, then let AI suggest a bilingual title & excerpt."}
        </div>
        <AiSuggestButton
          getContent={() => form.content_en || form.content_ar || form.excerpt_en || form.excerpt_ar}
          onSuggested={(d) => setForm((f) => ({
            ...f,
            title_en: f.title_en || d.title_en,
            title_ar: f.title_ar || d.title_ar,
            excerpt_en: f.excerpt_en || d.summary_en,
            excerpt_ar: f.excerpt_ar || d.summary_ar,
          }))}
          testid="blog-ai-suggest-btn"
        />
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <Field label={isRTL ? "العنوان (عربي)" : "Title (Arabic)"}>
          <Input value={form.title_ar} onChange={upd("title_ar")} required data-testid="title-ar" />
        </Field>
        <Field label={isRTL ? "العنوان (إنجليزي)" : "Title (English)"}>
          <Input value={form.title_en} onChange={upd("title_en")} required data-testid="title-en" />
        </Field>
      </div>

      <Field label={isRTL ? "Slug (للرابط مثل /blog/my-post)" : "Slug (/blog/my-post)"}>
        <Input value={form.slug} onChange={upd("slug")} placeholder="my-first-article" data-testid="slug" />
      </Field>

      <Field label={isRTL ? "صورة الغلاف" : "Cover image"}>
        <ImageUploader
          value={form.cover_image}
          onChange={(url) => setForm((f) => ({ ...f, cover_image: url }))}
          testid="blog-cover"
          aiPromptSuggestion={`${form.title_en || form.title_ar || "specialty coffee article"} — editorial blog cover, warm magazine light, soft focus, dark espresso tones`}
        />
      </Field>

      <Field label={isRTL ? "الوسوم (مفصولة بفاصلة)" : "Tags (comma separated)"}>
        <Input value={form.tags} onChange={upd("tags")} placeholder="إسبريسو, تحميص, V60" data-testid="tags" />
      </Field>

      <Field label={isRTL ? "مقتطف (عربي)" : "Excerpt (Arabic)"}>
        <Textarea value={form.excerpt_ar} onChange={upd("excerpt_ar")} rows={2} required data-testid="excerpt-ar" />
      </Field>
      <Field label={isRTL ? "مقتطف (إنجليزي)" : "Excerpt (English)"}>
        <Textarea value={form.excerpt_en} onChange={upd("excerpt_en")} rows={2} required data-testid="excerpt-en" />
      </Field>

      <Field label={isRTL ? "المحتوى (عربي)" : "Content (Arabic)"}>
        <Textarea value={form.content_ar} onChange={upd("content_ar")} rows={14} required data-testid="content-ar" />
      </Field>
      <Field label={isRTL ? "المحتوى (إنجليزي)" : "Content (English)"}>
        <Textarea value={form.content_en} onChange={upd("content_en")} rows={14} required data-testid="content-en" />
      </Field>

      <Button
        type="submit"
        disabled={submitting}
        className="w-full h-12 rounded-xl bg-[#C86240] hover:bg-[#b15637] text-white mt-6"
        data-testid="submit-post"
      >
        {submitting ? t.common.loading : (isEdit ? (isRTL ? "حفظ التعديلات" : "Save changes") : (isRTL ? "نشر المقال" : "Publish post"))}
      </Button>
    </form>
  );
}

const Field = ({ label, children }) => (
  <div className="mt-4">
    <Label className="text-[#2A2421] font-medium">{label}</Label>
    <div className="mt-2">{children}</div>
  </div>
);
